// B3.6 / TOUR-4a — tour layer: fly between steps, then highlight + callout.

import { createPortal } from "react-dom";
import { FireflyTourStepCallout } from "./FireflyTourStepCallout";
import { FireflyTourTravelFirefly } from "./firefly/FireflyTourTravelFirefly";
import { useFireflyTargetHighlight } from "./firefly/useFireflyTargetHighlight";
import { usePrefersReducedMotion } from "./firefly/usePrefersReducedMotion";
import type { FireflyTourSessionView } from "./firefly/useFireflyTourSession";
import type { FireflyIntent } from "./firefly/fireflyIntents";

export type FireflyTourSessionLayerProps = {
  session: FireflyTourSessionView;
};

export function FireflyTourSessionLayer({ session }: FireflyTourSessionLayerProps) {
  const reducedMotion = usePrefersReducedMotion();
  const intent = (session.travelIntent ?? session.intent ?? "teaching") as Extract<
    FireflyIntent,
    "teaching" | "discovery" | "guidance"
  >;

  const showHighlight =
    session.isActive &&
    !session.isReturningHome &&
    session.highlightReady &&
    Boolean(session.highlightTargetId);

  const { overlay } = useFireflyTargetHighlight({
    targetId: session.highlightTargetId,
    active: showHighlight,
    intent,
  });

  const showCallout =
    session.isActive &&
    !session.isReturningHome &&
    Boolean(session.stepBody);

  const callout = showCallout ? (
    <FireflyTourStepCallout
      title={session.stepTitle}
      body={session.stepBody}
      stepIndex={session.stepIndex}
      stepCount={session.stepCount}
      canAdvance={session.canAdvance}
      waitForUserAction={session.waitForUserAction}
      describeOnly={session.describeOnly}
      onNext={session.nextStep}
      onCancel={session.cancelTour}
    />
  ) : null;

  if (!session.isActive) return null;

  return (
    <>
      {session.isTraveling && !reducedMotion ? (
        <FireflyTourTravelFirefly
          viewportX={session.travelPosition.x}
          viewportY={session.travelPosition.y}
          intent={intent}
        />
      ) : null}
      {showHighlight ? overlay : null}
      {callout && typeof document !== "undefined"
        ? createPortal(callout, document.body)
        : callout}
    </>
  );
}
